import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Gift, Save, Plus, Trash2, Info, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_PROMO = {
  enabled: false,
  apply_to_pickup: false,
  show_progress_bar: true,
  tiers: [],
};

const DEFAULT_TIER = {
  min_subtotal: 50,
  discount_type: 'free',
  discount_value: 0,
  label: 'Frete Grátis',
};

/**
 * Aplica a promoção de frete progressiva. Espelha _apply_shipping_promo do backend.
 */
function applyShippingPromoJS(baseFee, subtotal, promo, deliveryType = 'delivery') {
  const b = Number(baseFee || 0);
  const s = Number(subtotal || 0);
  const result = {
    delivery_fee: +b.toFixed(2),
    original_fee: +b.toFixed(2),
    promo_applied: null,
    amount_to_next_tier: 0,
    next_tier: null,
  };
  if (!promo || !promo.enabled) return result;
  if (deliveryType === 'pickup' && !promo.apply_to_pickup) return result;
  const rawTiers = Array.isArray(promo.tiers) ? promo.tiers : [];
  const tiers = rawTiers
    .map((t) => ({ ...t, min_subtotal: Number(t.min_subtotal || 0) }))
    .filter((t) => t.min_subtotal >= 0)
    .sort((a, b) => a.min_subtotal - b.min_subtotal);
  if (!tiers.length) return result;
  let active = null;
  for (const t of tiers) {
    if (s >= t.min_subtotal) active = t; else break;
  }
  const next = tiers.find((t) => s < t.min_subtotal) || null;
  if (next) {
    result.next_tier = next;
    result.amount_to_next_tier = +Math.max(0, next.min_subtotal - s).toFixed(2);
  }
  if (!active) return result;
  const dtype = (active.discount_type || 'free').toLowerCase();
  const dval = Number(active.discount_value || 0);
  let newFee = b;
  if (dtype === 'free') newFee = 0;
  else if (dtype === 'percentage') {
    const pct = Math.max(0, Math.min(100, dval));
    newFee = b * (1 - pct / 100);
  } else if (dtype === 'fixed') {
    newFee = Math.max(0, b - Math.max(0, dval));
  } else newFee = 0;
  result.delivery_fee = +newFee.toFixed(2);
  result.promo_applied = {
    min_subtotal: active.min_subtotal,
    discount_type: dtype,
    discount_value: dval,
    label: active.label || null,
    savings: +(b - newFee).toFixed(2),
  };
  return result;
}

const ShippingPromoConfig = () => {
  const [promo, setPromo] = useState(DEFAULT_PROMO);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [simSubtotal, setSimSubtotal] = useState(45);
  const [simBaseFee, setSimBaseFee] = useState(10);

  const token = () => localStorage.getItem('token');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/delivery-config/shipping-promo`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        setPromo({ ...DEFAULT_PROMO, ...r.data, tiers: r.data.tiers || [] });
      } catch (e) {
        console.error('Erro ao carregar shipping-promo:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const simulation = useMemo(
    () => applyShippingPromoJS(simBaseFee, simSubtotal, promo),
    [simBaseFee, simSubtotal, promo]
  );

  const addTier = () => {
    setPromo((p) => ({ ...p, tiers: [...(p.tiers || []), { ...DEFAULT_TIER }] }));
  };

  const updateTier = (idx, field, value) => {
    setPromo((p) => {
      const t = [...p.tiers];
      t[idx] = { ...t[idx], [field]: value };
      if (field === 'discount_type' && value === 'free') t[idx].discount_value = 0;
      return { ...p, tiers: t };
    });
  };

  const removeTier = (idx) => {
    setPromo((p) => ({ ...p, tiers: p.tiers.filter((_, i) => i !== idx) }));
  };

  const validate = () => {
    if (!promo.enabled) return null;
    if (!promo.tiers.length) return 'Adicione pelo menos uma faixa quando a promoção estiver ativa.';
    for (const [i, t] of promo.tiers.entries()) {
      const m = Number(t.min_subtotal);
      if (!(m > 0)) return `Faixa ${i + 1}: valor mínimo deve ser maior que zero.`;
      const dtype = t.discount_type;
      const dval = Number(t.discount_value || 0);
      if (dtype === 'percentage' && !(dval >= 1 && dval <= 100)) {
        return `Faixa ${i + 1}: percentual deve estar entre 1 e 100.`;
      }
      if (dtype === 'fixed' && !(dval > 0)) {
        return `Faixa ${i + 1}: valor de desconto fixo deve ser maior que zero.`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    setSuccess('');
    setError('');
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/delivery-config/shipping-promo`,
        { shipping_promo: promo },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setSuccess('Promoção de frete salva com sucesso!');
      setTimeout(() => setSuccess(''), 3500);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || 'Erro ao salvar promoção.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-6" data-testid="shipping-promo-config">
        <p className="text-gray-400">Carregando promoção de frete…</p>
      </div>
    );
  }

  const savings = simulation.promo_applied?.savings || 0;

  return (
    <div className="glass-card p-6 mt-6" data-testid="shipping-promo-config">
      <div className="flex items-center gap-3 mb-4">
        <Gift className="w-6 h-6 text-green-400" />
        <div>
          <h3 className="text-lg font-bold text-white">Promoção de Frete Progressiva</h3>
          <p className="text-xs text-gray-400">Recompense pedidos maiores com frete grátis ou desconto no frete.</p>
        </div>
      </div>

      {/* Master toggle */}
      <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-purple-400" />
          <span className="text-white font-medium">Ativar promoção</span>
        </div>
        <button
          type="button"
          data-testid="shipping-promo-enabled-toggle"
          onClick={() => setPromo({ ...promo, enabled: !promo.enabled })}
          className={`relative w-14 h-8 rounded-full transition-colors ${promo.enabled ? 'bg-green-500' : 'bg-gray-600'}`}
          aria-pressed={promo.enabled}
        >
          <span
            className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow transform transition-transform ${promo.enabled ? 'translate-x-6' : ''}`}
          />
        </button>
      </div>

      {/* Options */}
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <label className="flex items-center gap-2 p-3 bg-white/5 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            data-testid="shipping-promo-apply-pickup"
            checked={promo.apply_to_pickup}
            onChange={(e) => setPromo({ ...promo, apply_to_pickup: e.target.checked })}
          />
          <span className="text-sm text-gray-200">Aplicar também para retirada</span>
        </label>
        <label className="flex items-center gap-2 p-3 bg-white/5 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            data-testid="shipping-promo-show-progress"
            checked={promo.show_progress_bar}
            onChange={(e) => setPromo({ ...promo, show_progress_bar: e.target.checked })}
          />
          <span className="text-sm text-gray-200">Mostrar barra de progresso no cardápio</span>
        </label>
      </div>

      {/* Tiers */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-white">Faixas de desconto</p>
          <button
            type="button"
            data-testid="shipping-promo-add-tier"
            onClick={addTier}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-500/20 border border-purple-500/40 rounded-md text-purple-300 text-xs font-medium hover:bg-purple-500/30"
          >
            <Plus className="w-3 h-3" /> Adicionar faixa
          </button>
        </div>
        <div className="space-y-2">
          {(promo.tiers || []).map((t, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 p-2 bg-white/5 rounded-lg items-center">
              <input
                data-testid={`tier-${idx}-min-subtotal`}
                type="number"
                min="1"
                step="0.01"
                value={t.min_subtotal}
                onChange={(e) => updateTier(idx, 'min_subtotal', parseFloat(e.target.value) || 0)}
                placeholder="Mín. R$"
                className="glass-input col-span-3 sm:col-span-2 text-sm px-2 py-1.5"
              />
              <select
                data-testid={`tier-${idx}-type`}
                value={t.discount_type}
                onChange={(e) => updateTier(idx, 'discount_type', e.target.value)}
                className="glass-input col-span-3 sm:col-span-2 text-sm px-2 py-1.5"
              >
                <option value="free">Grátis</option>
                <option value="percentage">% off</option>
                <option value="fixed">R$ off</option>
              </select>
              <input
                data-testid={`tier-${idx}-value`}
                type="number"
                min="0"
                step="0.01"
                value={t.discount_value || 0}
                disabled={t.discount_type === 'free'}
                onChange={(e) => updateTier(idx, 'discount_value', parseFloat(e.target.value) || 0)}
                placeholder="Valor"
                className="glass-input col-span-3 sm:col-span-2 text-sm px-2 py-1.5 disabled:opacity-50"
              />
              <input
                data-testid={`tier-${idx}-label`}
                type="text"
                value={t.label || ''}
                onChange={(e) => updateTier(idx, 'label', e.target.value)}
                placeholder="Rótulo (opcional)"
                className="glass-input col-span-3 sm:col-span-5 text-sm px-2 py-1.5"
              />
              <button
                type="button"
                data-testid={`tier-${idx}-remove`}
                onClick={() => removeTier(idx)}
                className="col-span-12 sm:col-span-1 inline-flex items-center justify-center p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-md"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {!(promo.tiers || []).length && (
            <p className="text-xs text-gray-500 italic">Nenhuma faixa cadastrada. Clique em "Adicionar faixa".</p>
          )}
        </div>
      </div>

      {/* Simulator */}
      <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4 text-purple-400" />
          <p className="text-sm font-medium text-purple-200">Simulador em tempo real</p>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="flex flex-col gap-1 text-xs text-gray-300">
            Subtotal (R$)
            <input
              data-testid="shipping-promo-sim-subtotal"
              type="number" min="0" step="0.01" value={simSubtotal}
              onChange={(e) => setSimSubtotal(parseFloat(e.target.value) || 0)}
              className="glass-input text-sm px-2 py-1.5"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-300">
            Frete base (R$)
            <input
              data-testid="shipping-promo-sim-base-fee"
              type="number" min="0" step="0.01" value={simBaseFee}
              onChange={(e) => setSimBaseFee(parseFloat(e.target.value) || 0)}
              className="glass-input text-sm px-2 py-1.5"
            />
          </label>
        </div>
        <div className="text-sm text-gray-200">
          {simulation.promo_applied ? (
            <>
              <p>
                Frete original: <span className="line-through text-gray-500">R$ {simulation.original_fee.toFixed(2)}</span>{' '}
                → <span className="text-green-400 font-bold">{simulation.delivery_fee === 0 ? 'GRÁTIS' : `R$ ${simulation.delivery_fee.toFixed(2)}`}</span>
              </p>
              {simulation.promo_applied.label && (
                <p className="text-xs text-green-300 mt-1">🎁 {simulation.promo_applied.label}</p>
              )}
              {savings > 0 && (
                <p className="text-xs text-gray-400 mt-1">Cliente economiza R$ {savings.toFixed(2)}</p>
              )}
            </>
          ) : simulation.next_tier ? (
            <>
              <p>
                Frete: <span className="text-white font-bold">R$ {simulation.delivery_fee.toFixed(2)}</span>
              </p>
              <p className="text-xs text-purple-300 mt-1">
                Faltam <b>R$ {simulation.amount_to_next_tier.toFixed(2)}</b> para{' '}
                {simulation.next_tier.label || `faixa de R$ ${simulation.next_tier.min_subtotal.toFixed(2)}`}
              </p>
            </>
          ) : (
            <p>Sem promoção aplicável.</p>
          )}
        </div>
      </div>

      {error && (
        <p data-testid="shipping-promo-error" className="text-sm text-red-400 mb-3">{error}</p>
      )}
      {success && (
        <p data-testid="shipping-promo-success" className="text-sm text-green-400 mb-3">{success}</p>
      )}

      <button
        type="button"
        data-testid="shipping-promo-save-btn"
        onClick={handleSave}
        disabled={saving}
        className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 disabled:opacity-60 rounded-lg text-white font-medium"
      >
        <Save className="w-4 h-4" />
        {saving ? 'Salvando…' : 'Salvar promoção'}
      </button>
    </div>
  );
};

export default ShippingPromoConfig;
